import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.tsx';
import HelpFAB from './HelpFAB.tsx';
import {
  HomeIcon,
  CogIcon,
  TruckIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  PuzzlePieceIcon,
  BellIcon,
} from '@heroicons/react/24/outline';


export const Layout: React.FC = () => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, customer, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    
    { name: t('navigation.dashboard'), href: '/app/dashboard', icon: HomeIcon },
    { name: t('navigation.equipment'), href: '/app/equipment', icon: CogIcon },
    { name: t('navigation.suppliers'), href: '/app/suppliers', icon: TruckIcon },
    { name: t('navigation.sites'), href: '/app/sites', icon: MapPinIcon },
    { name: t('navigation.hires'), href: '/app/hires', icon: ClipboardDocumentListIcon },
    { name: t('navigation.hiresMap'), href: '/app/hires-map', icon: MapPinIcon },
    { name: t('navigation.reminders'), href: '/app/reminders', icon: BellIcon },
    { name: t('navigation.integrations'), href: '/app/integrations', icon: PuzzlePieceIcon },
    { name: t('navigation.users'), href: '/app/users', icon: UsersIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-base-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-base-100">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <Link to="/app/dashboard" className="flex items-center" onClick={() => setSidebarOpen(false)}>
                <img 
                  src="/images/site_logo.png" 
                  alt="Planted" 
                  className="h-8 w-auto hover:opacity-80 transition-opacity"
                />
              </Link>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                  >
                    <item.icon
                      className={`${
                        isActive ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                      } mr-4 flex-shrink-0 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center">
              <div>
                <div className="text-base font-medium text-gray-800 dark:text-gray-200">{user?.name}</div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{customer?.name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 dark:border-gray-700 bg-base-100">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <Link to="/app/dashboard" className="flex items-center">
                  <img 
                    src="/images/site_logo.png" 
                    alt="Planted" 
                    className="h-8 w-auto hover:opacity-80 transition-opacity"
                  />
                </Link>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        isActive
                          ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                    >
                      <item.icon
                        className={`${
                          isActive ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                        } mr-3 flex-shrink-0 h-6 w-6`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center w-full">
                <div className="flex-shrink-0">
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{user?.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{customer?.name}</div>
                  <div className="flex items-center mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      customer?.status === 'free' 
                        ? 'bg-blue-100 text-blue-800'
                        : customer?.status === 'active'
                        ? 'bg-success-100 text-success-800'
                        : 'bg-base-200 text-gray-800'
                    }`}>
                      {customer?.status === 'free' ? 'Free Edition' : customer?.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 p-1 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-base-100 shadow dark:shadow-gray-800">
          <button
            type="button"
            className="px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full text-gray-400 dark:text-gray-500 focus-within:text-gray-600 dark:focus-within:text-gray-400">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-5 w-5" />
                  </div>
                  <div className="flex items-center h-full pl-8">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {location.pathname === '/dashboard' && 'Dashboard'}
                      {location.pathname === '/equipment' && 'Equipment Management'}
                      {location.pathname === '/suppliers' && 'Suppliers'}
                      {location.pathname === '/sites' && 'Sites'}
                      {location.pathname === '/hires' && 'Equipment Hires'}
                      {location.pathname === '/users' && 'User Management'}
                      {location.pathname === '/integrations' && 'Integrations'}
                      {location.pathname === '/app/profile' && 'Profile'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <Link
                to="/app/profile"
                className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {user?.profile_picture_url ? (
                  <img
                    src={user.profile_picture_url}
                    alt="Profile"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <UserCircleIcon className="h-6 w-6" />
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      
      {/* Help FAB - appears on all pages */}
      <HelpFAB />
    </div>
  );
};
